import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, useLanguage } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Timer, Settings, Info, X, Check, Loader2, Droplets, Coffee,
  Moon, Sun, ChevronDown, UtensilsCrossed
} from 'lucide-react';
import { FastingCompletePopup } from './FastingGuard';

const PROTOCOLS = [
  { key: '16:8', label: '16:8', fasting: 16, eating: 8, desc: 'Standard' },
  { key: '14:10', label: '14:10', fasting: 14, eating: 10, desc: 'Debutant' },
  { key: '18:6', label: '18:6', fasting: 18, eating: 6, desc: 'Avance' },
  { key: '20:4', label: '20:4', fasting: 20, eating: 4, desc: 'Guerrier' },
];

/**
 * Compute fasting status LOCALLY using the browser's Date object.
 * All times are in the user's local timezone.
 */
function computeLocalStatus(settings) {
  if (!settings || !settings.is_active) {
    return { is_active: false, in_fasting_window: false };
  }
  const now = new Date();
  const fastH = settings.fasting_hours || 16;
  const eatH = settings.eating_hours || 8;
  const startH = settings.start_hour ?? 20;
  const startM = settings.start_minute ?? 0;

  const todayStart = new Date(now);
  todayStart.setHours(startH, startM, 0, 0);

  const cycleStart = now < todayStart
    ? new Date(todayStart.getTime() - 86400000)
    : todayStart;

  const elapsedMs = now.getTime() - cycleStart.getTime();
  const elapsedH = elapsedMs / 3600000;
  const fastEnd = new Date(cycleStart.getTime() + fastH * 3600000);
  const eatEnd = new Date(fastEnd.getTime() + eatH * 3600000);

  if (elapsedH < fastH) {
    const pct = Math.min(100, (elapsedH / fastH) * 100);
    const rem = Math.max(0, Math.round((fastEnd - now) / 60000));
    return { is_active: true, in_fasting_window: true, phase: 'fasting', progress_pct: Math.round(pct * 10) / 10, time_remaining_minutes: rem, protocol: settings.protocol || '16:8' };
  } else {
    const eatElapsed = elapsedH - fastH;
    const pct = Math.min(100, (eatElapsed / eatH) * 100);
    const rem = Math.max(0, Math.round((eatEnd - now) / 60000));
    return { is_active: true, in_fasting_window: false, phase: 'eating', progress_pct: Math.round(pct * 10) / 10, time_remaining_minutes: rem, protocol: settings.protocol || '16:8' };
  }
}

function CircularProgress({ progress, size = 120, strokeWidth = 8, phase, timeText, label }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  const gradientId = phase === 'fasting' ? 'fasting-grad' : 'eating-grad';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size, filter: progress > 0 ? `drop-shadow(${phase === 'eating' ? '0 0 10px rgba(52,211,153,0.7)' : '0 0 8px rgba(99,102,241,0.6)'})` : 'none' }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="fasting-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="eating-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="currentColor" strokeWidth={strokeWidth}
          className="text-secondary/40"
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={`url(#${gradientId})`} strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-black tracking-tight">{timeText}</span>
        <span className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
}

export default function FastingWidget({ hideWhenInactive = false }) {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [saving, setSaving] = useState(false);
  // Settings form
  const [formProtocol, setFormProtocol] = useState('16:8');
  const [formFastingH, setFormFastingH] = useState(16);
  const [formEatingH, setFormEatingH] = useState(8);
  const [formStartHour, setFormStartHour] = useState(20);
  const [formStartMin, setFormStartMin] = useState(0);
  const [formActive, setFormActive] = useState(false);
  const timerRef = useRef(null);
  const prevPhaseRef = useRef(null);
  const [showComplete, setShowComplete] = useState(false);
  const [eatingWindowMin, setEatingWindowMin] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const settingsRes = await api.get('/fasting/settings');
      const s = settingsRes.data.settings;
      setSettings(s);
      setFormProtocol(s.protocol || '16:8');
      setFormFastingH(s.fasting_hours || 16);
      setFormEatingH(s.eating_hours || 8);
      setFormStartHour(s.start_hour ?? 20);
      setFormStartMin(s.start_minute ?? 0);
      setFormActive(s.is_active || false);
      // Compute status locally
      setStatus(computeLocalStatus(s));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live countdown — computed locally every second for smooth updates
  useEffect(() => {
    if (!settings?.is_active) return;
    timerRef.current = setInterval(() => {
      const newStatus = computeLocalStatus(settings);
      // Detect transition from fasting -> eating (fast completed!)
      if (prevPhaseRef.current === 'fasting' && newStatus.phase === 'eating') {
        setEatingWindowMin(newStatus.time_remaining_minutes || 0);
        setShowComplete(true);
        // Auto-call break endpoint to log success
        api.post('/fasting/break', { reason: 'timer_ended', calories: 0 }).catch(() => {});
      }
      prevPhaseRef.current = newStatus.phase;
      setStatus(newStatus);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/fasting/settings', {
        is_active: formActive,
        protocol: formProtocol,
        fasting_hours: formFastingH,
        eating_hours: formEatingH,
        start_hour: formStartHour,
        start_minute: formStartMin,
      });
      setSettings(res.data.settings);
      setShowSettings(false);
      toast.success(fr ? 'Paramètres de jeûne sauvegardes!' : 'Fasting settings saved!');
      // Refresh locally
      setStatus(computeLocalStatus(res.data.settings));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleProtocolSelect = (p) => {
    setFormProtocol(p.key);
    setFormFastingH(p.fasting);
    setFormEatingH(p.eating);
  };

  const formatTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
    return `${m}m`;
  };

  if (loading) return null;

  // Yuri spec 2026-05-06: on the Dashboard we hide the whole widget when the
  // user hasn't activated fasting. They can still enable it from Settings /
  // Advanced Analysis, but the main view stays focused on what's active.
  if (hideWhenInactive && !settings?.is_active && !showSettings) {
    return null;
  }

  // Not active — show activation card
  if (!settings?.is_active && !showSettings) {
    return (
      <Card className="bg-card/60 border-border border-dashed" data-testid="fasting-widget-inactive">
        <CardContent className="p-4 flex items-center gap-3 relative overflow-hidden">
          {/* GLOW upgrade (Iter. 388) — indigo halo to invite activation */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 30% 50%, rgba(99,102,241,0.18), transparent 70%)',
            }}
          />
          <div
            className="w-10 h-10 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0 relative z-10"
            style={{ boxShadow: '0 0 12px rgba(99,102,241,0.35), inset 0 0 6px rgba(99,102,241,0.20)' }}
          >
            <Timer className="w-5 h-5 text-indigo-300" style={{ filter: 'drop-shadow(0 0 4px rgba(99,102,241,0.8))' }} />
          </div>
          <div className="flex-1 min-w-0 relative z-10">
            <p className="text-xs font-bold">{fr ? 'Jeûne Intermittent' : 'Intermittent Fasting'}</p>
            <p className="text-[10px] text-muted-foreground">
              {fr ? 'Active le suivi de ton jeûne pour synchroniser l\'IA' : 'Activate fasting tracking to sync with AI'}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setFormActive(true); setShowSettings(true); }}
            className="text-[10px] border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/15 relative z-10"
            style={{ boxShadow: '0 0 10px rgba(99,102,241,0.25)' }}
            data-testid="activate-fasting-btn"
          >
            <Timer className="w-3 h-3 mr-1" /> {fr ? 'Activer' : 'Activate'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isFasting = status?.in_fasting_window;
  const phase = status?.phase || 'eating';
  const progress = status?.progress_pct || 0;
  const remaining = status?.time_remaining_minutes || 0;

  // Yuri spec 2026-02 (final neon phase): the eating window must FLASH like
  // a green light at a starting line — emerald glow signature, mirroring the
  // blue-glow of hydration. Fasting phase keeps a calmer indigo glow so the
  // user instantly reads which mode is active.
  const EMERALD_GLOW = '0 0 14px rgba(52,211,153,0.55), 0 0 4px rgba(16,185,129,0.45)';
  const EMERALD_GLOW_STRONG = '0 0 22px rgba(52,211,153,0.78), 0 0 10px rgba(16,185,129,0.65)';
  const INDIGO_GLOW = '0 0 12px rgba(99,102,241,0.4), 0 0 4px rgba(79,70,229,0.35)';
  const cardStyle = isFasting
    ? { borderColor: 'rgba(99,102,241,0.4)', boxShadow: INDIGO_GLOW, background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(79,70,229,0.04))' }
    : { borderColor: 'rgba(52,211,153,0.55)', boxShadow: EMERALD_GLOW_STRONG, background: 'linear-gradient(135deg, rgba(52,211,153,0.14), rgba(16,185,129,0.06))' };

  return (
    <div data-testid="fasting-widget">
      {/* Main Widget */}
      {settings?.is_active && status?.is_active && (
        <Card className="border" style={cardStyle} data-testid="fasting-tracker">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {isFasting ? <Moon className="w-4 h-4 text-indigo-400" /> : <Sun className="w-4 h-4 text-emerald-400" />}
                <span className="text-xs font-heading uppercase tracking-wider">
                  {isFasting
                    ? (fr ? 'Jeûne en cours' : 'Fasting')
                    : (fr ? 'Fenêtre alimentaire' : 'Eating Window')
                  }
                </span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                  isFasting ? 'bg-indigo-500/20 text-indigo-400' : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {settings.protocol}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowInfo(true)}
                  className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  data-testid="fasting-info-btn"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  data-testid="fasting-settings-btn"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <CircularProgress
                progress={progress}
                size={100}
                strokeWidth={7}
                phase={phase}
                timeText={formatTime(remaining)}
                label={fr ? 'restant' : 'remaining'}
              />
              <div className="flex-1 space-y-2">
                {isFasting ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Droplets className="w-3 h-3 text-blue-400" />
                      {fr ? 'Reste hydraté! Eau, thé, café noir.' : 'Stay hydrated! Water, tea, black coffee.'}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Coffee className="w-3 h-3 text-amber-400" />
                      {fr ? 'Electrolytes: sel rose, magnésium' : 'Electrolytes: pink salt, magnésium'}
                    </div>
                    <div className="text-[9px] text-indigo-400/80 italic mt-1">
                      {fr ? 'L\'IA est synchronisee — aucune suggestion alimentaire' : 'AI synced — no food suggestions'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Sun className="w-3 h-3 text-emerald-400" />
                      {fr ? 'Fenêtre ouverte! Maximise tes protéines.' : 'Window open! Maximize your protein.'}
                    </div>
                    <div className="text-[9px] text-emerald-400/80 italic mt-1">
                      {fr ? 'L\'IA suggerera des repas optimaux' : 'AI will suggest optimal meals'}
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-border rounded-xl p-5 w-full max-w-sm space-y-4 shadow-xl max-h-[85vh] overflow-y-auto"
              data-testid="fasting-settings-modal"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                  <Timer className="w-4 h-4 text-indigo-400" />
                  {fr ? 'Paramètres de jeûne' : 'Fasting Settings'}
                </h3>
                <button onClick={() => setShowSettings(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
              </div>

              {/* Protocol Selection */}
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  {fr ? 'Protocole' : 'Protocol'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PROTOCOLS.map(p => (
                    <button
                      key={p.key}
                      onClick={() => handleProtocolSelect(p)}
                      data-testid={`protocol-${p.key}`}
                      className={`p-2.5 rounded-lg border text-center transition-all ${
                        formProtocol === p.key
                          ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-400'
                          : 'bg-secondary/30 border-border text-muted-foreground hover:border-indigo-500/20'
                      }`}
                    >
                      <span className="text-sm font-black block">{p.label}</span>
                      <span className="text-[9px] block mt-0.5 opacity-70">{p.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Hours */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">{fr ? 'Heures de jeûne' : 'Fasting hours'}</label>
                  <Input
                    type="number" min={10} max={23}
                    value={formFastingH}
                    onChange={(e) => { const v = parseInt(e.target.value) || 16; setFormFastingH(v); setFormEatingH(24 - v); setFormProtocol('custom'); }}
                    className="text-center font-bold"
                    data-testid="fasting-hours-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">{fr ? 'Fenêtre alimentaire' : 'Eating window'}</label>
                  <Input
                    type="number" min={1} max={14}
                    value={formEatingH}
                    onChange={(e) => { const v = parseInt(e.target.value) || 8; setFormEatingH(v); setFormFastingH(24 - v); setFormProtocol('custom'); }}
                    className="text-center font-bold"
                    data-testid="eating-hours-input"
                  />
                </div>
              </div>

              {/* Start Time */}
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">
                  {fr ? 'Heure de début du jeûne' : 'Fasting start time'}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min={0} max={23}
                    value={formStartHour}
                    onChange={(e) => setFormStartHour(parseInt(e.target.value) || 0)}
                    className="w-20 text-center font-bold"
                    data-testid="start-hour-input"
                  />
                  <span className="text-lg font-bold text-muted-foreground">:</span>
                  <Input
                    type="number" min={0} max={59} step={15}
                    value={formStartMin}
                    onChange={(e) => setFormStartMin(parseInt(e.target.value) || 0)}
                    className="w-20 text-center font-bold"
                    data-testid="start-min-input"
                  />
                  <span className="text-xs text-muted-foreground">
                    ({fr ? `jeûne de ${formStartHour}:${formStartMin.toString().padStart(2, '0')} a ${(formStartHour + formFastingH) % 24}:${formStartMin.toString().padStart(2, '0')}` : `fast ${formStartHour}:${formStartMin.toString().padStart(2, '0')} to ${(formStartHour + formFastingH) % 24}:${formStartMin.toString().padStart(2, '0')}`})
                  </span>
                </div>
              </div>

              {/* Active toggle */}
              <label className={`flex items-center gap-2 cursor-pointer p-2.5 rounded-lg border transition-all ${
                formActive ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-secondary/20 border-border'
              }`}>
                <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} className="sr-only" />
                <div className={`w-9 h-5 rounded-full transition-all relative ${formActive ? 'bg-indigo-500' : 'bg-zinc-600'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${formActive ? 'left-4.5' : 'left-0.5'}`} />
                </div>
                <span className="text-xs font-bold">{fr ? 'Activer le suivi' : 'Activate tracking'}</span>
              </label>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowSettings(false)} className="flex-1" data-testid="fasting-settings-cancel">
                  {fr ? 'Annuler' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="fasting-settings-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  {fr ? 'Sauvegarder' : 'Save'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Modal */}
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowInfo(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-border rounded-xl p-5 w-full max-w-sm space-y-3 shadow-xl"
              data-testid="fasting-info-modal"
            >
              <h3 className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                <Info className="w-4 h-4 text-indigo-400" />
                {fr ? 'Guide du Jeûne Intermittent' : 'Intermittent Fasting Guide'}
              </h3>

              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                  <p className="font-bold text-emerald-400 text-[10px] uppercase mb-1">{fr ? 'Permis pendant le jeûne' : 'Allowed during fast'}</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-1.5"><Droplets className="w-3 h-3 text-blue-400" /> {fr ? 'Eau (plate, petillante, citronnee)' : 'Water (flat, sparkling, with lemon)'}</li>
                    <li className="flex items-center gap-1.5"><Coffee className="w-3 h-3 text-amber-400" /> {fr ? 'Café noir (sans sucre ni crème)' : 'Black coffee (no sugar or cream)'}</li>
                    <li className="flex items-center gap-1.5"><span className="w-3 h-3 text-center text-[10px]">🍵</span> {fr ? 'The nature (vert, noir, tisane)' : 'Plain tea (green, black, herbal)'}</li>
                    <li className="flex items-center gap-1.5"><span className="w-3 h-3 text-center text-[10px]">🧂</span> {fr ? 'Electrolytes (sel rose, magnésium)' : 'Electrolytes (pink salt, magnésium)'}</li>
                  </ul>
                </div>
                <div className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/20">
                  <p className="font-bold text-red-400 text-[10px] uppercase mb-1">{fr ? 'Interdit pendant le jeûne' : 'Not allowed during fast'}</p>
                  <ul className="space-y-1">
                    <li>{fr ? 'Tout aliment solide ou calorique' : 'Any solid or caloric food'}</li>
                    <li>{fr ? 'Boissons sucrees, jus, lait' : 'Sugary drinks, juice, milk'}</li>
                    <li>{fr ? 'Supplements caloriques (BCAA, whey)' : 'Caloric supplements (BCAA, whey)'}</li>
                  </ul>
                </div>
                <div className="p-2.5 rounded-lg bg-indigo-500/5 border border-indigo-500/20">
                  <p className="font-bold text-indigo-400 text-[10px] uppercase mb-1">{fr ? 'Bienfaits' : 'Benefits'}</p>
                  <ul className="space-y-1">
                    <li>{fr ? 'Autophagie cellulaire (nettoyage)' : 'Cellular autophagy (cleansing)'}</li>
                    <li>{fr ? 'Amelioration sensibilite a l\'insuline' : 'Improved insulin sensitivity'}</li>
                    <li>{fr ? 'Clarté mentale accrue' : 'Increased mental clarity'}</li>
                    <li>{fr ? 'Optimisation hormonale' : 'Hormonal optimization'}</li>
                  </ul>
                </div>
              </div>

              <Button onClick={() => setShowInfo(false)} className="w-full" variant="outline" data-testid="fasting-info-close">
                {fr ? 'Compris!' : 'Got it!'}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* End-of-Fast Completion Popup */}
      <FastingCompletePopup
        open={showComplete}
        onClose={(action) => {
          setShowComplete(false);
          if (action === 'log_meal') {
            // Navigate to add meal (scroll to top and open meal options)
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
        eatingWindowMinutes={eatingWindowMin}
        language={language}
      />
    </div>
  );
}
